import { Checkbox, Icon, Tooltip } from "@blueprintjs/core";
import { useScenarioResults } from "in_queue/contexts/ScenarioResultsContext";
import {
    getConstraintStudyCycle,
    isPriorQueueConstraint,
    ScenarioConstraint
} from "in_queue/types/scenarioResultType";
import { mapLoadingState } from "types/loadingType";

import { AgGridReact } from "ag-grid-react";
import { segmentTrackToggleInQueueTableFilter } from "in_queue/analytics";
import { useAdditionalColumnsForTable } from "in_queue/contexts/AdditionalColumnConfigsContext";
import { useCluster, useRegionConfig } from "in_queue/contexts/ClusterContext";
import { useCurrentProjectId } from "in_queue/contexts/ScenarioPageContext";
import { useScenarioResultValidViewTypes } from "in_queue/hooks/useScenarioResultViewTypes";
import { InQueueRegionConfig } from "in_queue/types/configPerRegion";
import { useEffect, useMemo, useRef, useState } from "react";
import { RegionType } from "types/regionType";
import { spliceAdditionalColumns } from "./agGrid/additionalColumns";
import {
    AgGridColumnDefs,
    ConstraintsTableColumnId,
    constraintTypeFormatter,
    dfaxFormatter,
    hideUnwantedColumns,
    percentValueFormatter,
    roundingFormatter,
    SCENARIO_TABLE_DEFAULT_COL_DEF,
    stringFormatter
} from "./agGrid/agGridHelpers";
import {
    formatConstraintStudyCycle,
    ScenarioConstraintQueueCellRenderer
} from "./agGrid/cellRenderers";
import {
    computeInScopePqKeys,
    doesConstraintPassFilter,
    isExternalConstraintFilterPresent
} from "./agGrid/scenarioConstraintsPqFilter";
import { BaseAgGrid } from "./base/BaseAgGrid";
import { ExportButton } from "./ribbon/ExportButton";
import { FilterSearchInput } from "./ribbon/FilterSearchInput";
import { ResultsCount } from "./ribbon/ResultsCount";
import { TableActionRibbon } from "./ribbon/TableActionRibbon";
import { ViewType, ViewTypesMenu } from "./ribbon/ViewTypesMenu";
import css from "./TableStyles.module.scss";

const SCENARIO_CONSTRAINTS_ANCHOR_TO_COL_ID: Readonly<
    Record<string, ConstraintsTableColumnId>
> = {
    project_id: "projectId",
    monitored_facility_name: "monitoredFacilityName",
    constraint_type: "constraintType",
    dfax: "dfax",
    mw_impact: "mwImpact",
    rating: "rating",
    worst_contingency: "worstContingency",
    network_upgrades: "networkUpgrades",
    notes: "notes"
};

export const ScenarioConstraintsTable: React.FC<{
    filterText: string;
    setFilterText: (value: string) => void;
    /** v2 sticky-header / no-inner-scroll mode — see ScenarioProjectsTable. */
    stickyHeader?: boolean;
}> = ({ filterText, setFilterText, stickyHeader }) => {
    const gridRef = useRef<AgGridReact<ScenarioConstraint>>(null);
    const { scenarioResults, isLoadingRemaining } = useScenarioResults();
    const componentConfig = useRegionConfig();
    const region = useCluster().region;
    const currentProjectId = useCurrentProjectId();
    const [viewType, setViewType] = useState<ViewType>("current");
    // Off by default: CQ competitors are the focus; PQ contributors are informational/opt-in.
    const [includePqContributors, setIncludePqContributors] = useState(false);
    const [visibleCount, setVisibleCount] = useState<number | undefined>(
        undefined
    );
    const validViewTypes = useScenarioResultValidViewTypes();
    const additionalColumnConfigs = useAdditionalColumnsForTable(
        "scenario_constraints"
    );

    const constraints = mapLoadingState(scenarioResults, (d) => d.constraints);

    // PQ rows are tagged in metadata; the Study Cycle column, PQ badge and toggle only show when present.
    const hasPriorQueue = useMemo(
        () =>
            constraints !== "loading" &&
            constraints.some((c) => isPriorQueueConstraint(c)),
        [constraints]
    );

    // (facility, contingency) keys of the in-view CQ constraints; a PQ row only shows (in the
    // narrowed views) if it overloads one of these. Tracks the view: current project's
    // constraints in "Only my project", current + related in "Related".
    const inScopePqKeys = useMemo(() => {
        if (constraints === "loading" || scenarioResults === "loading") {
            return new Set<string>();
        }
        return computeInScopePqKeys(constraints, {
            viewType,
            currentProjectId,
            relatedProjectIds: scenarioResults.relatedProjectIds
        });
    }, [constraints, scenarioResults, viewType, currentProjectId]);

    const columnDefs = useMemo(
        () =>
            spliceAdditionalColumns(
                getColumnDefs(componentConfig, hasPriorQueue, region),
                additionalColumnConfigs,
                SCENARIO_CONSTRAINTS_ANCHOR_TO_COL_ID
            ),
        [componentConfig, additionalColumnConfigs, hasPriorQueue, region]
    );

    // Re-run the external filter when its inputs change; api is undefined until onGridReady.
    useEffect(() => {
        const api = gridRef.current?.api;
        if (api) {
            api.onFilterChanged();
        }
    }, [viewType, includePqContributors, inScopePqKeys]);
    const totalCount =
        constraints !== "loading" && !isLoadingRemaining
            ? constraints.length
            : undefined;

    const leftActions = [
        <ViewTypesMenu
            currentViewType={viewType}
            setCurrentViewType={(newViewType: ViewType) => {
                segmentTrackToggleInQueueTableFilter({
                    tableName: "Constraints",
                    filterName: newViewType
                });
                setViewType(newViewType);
            }}
            validViewTypes={validViewTypes}
        />,
        ...(hasPriorQueue
            ? [
                  <div
                      style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          alignSelf: "center"
                      }}
                  >
                      <Checkbox
                          checked={includePqContributors}
                          label="Show PQ contributors"
                          style={{ marginBottom: 0, alignSelf: "center" }}
                          onChange={(e) => {
                              const checked = e.currentTarget.checked;
                              segmentTrackToggleInQueueTableFilter({
                                  tableName: "Constraints",
                                  filterName: `include_pq_contributors:${checked}`
                              });
                              setIncludePqContributors(checked);
                          }}
                      />
                      <Tooltip
                          content={
                              <div style={{ maxWidth: 260 }}>
                                  {
                                      "Prior-queue (PQ) projects are not part of the current cluster or cost allocation, but they still contribute to constraints. They are shown for informational purposes only."
                                  }
                              </div>
                          }
                          compact
                      >
                          <span style={{ display: "flex", cursor: "help" }}>
                              <Icon
                                  icon="info-sign"
                                  size={12}
                                  color="#5c7080"
                              />
                          </span>
                      </Tooltip>
                  </div>
              ]
            : [])
    ];
    const rightActions = [
        <ExportButton gridRef={gridRef} exportName="Constraints" />,
        <FilterSearchInput value={filterText} onChange={setFilterText} />
    ];
    return (
        <div className={css["table-ribbon-wrapper"]}>
            <TableActionRibbon
                leftActions={leftActions}
                rightActions={rightActions}
                sticky={stickyHeader}
            />
            <BaseAgGrid<ScenarioConstraint>
                ref={gridRef}
                loadingData={constraints}
                columnDefs={columnDefs}
                analyticsTableName="Constraints"
                stickyHeader={stickyHeader}
                defaultColDef={SCENARIO_TABLE_DEFAULT_COL_DEF}
                rowHeight={52}
                quickFilterText={filterText}
                onModelUpdated={(event) =>
                    setVisibleCount(event.api.getDisplayedRowCount())
                }
                isExternalFilterPresent={() =>
                    isExternalConstraintFilterPresent(viewType, hasPriorQueue)
                }
                doesExternalFilterPass={(node) => {
                    const row = node.data;
                    if (row == null) {
                        return false;
                    }
                    return doesConstraintPassFilter(row, {
                        viewType,
                        currentProjectId,
                        relatedProjectIds:
                            scenarioResults === "loading"
                                ? new Set<string>()
                                : scenarioResults.relatedProjectIds,
                        includePqContributors,
                        inScopePqKeys
                    });
                }}
            />
            <div className={css["table-footer"]}>
                <ResultsCount visible={visibleCount} total={totalCount} />
            </div>
        </div>
    );
};

const getColumnDefs = (
    config: InQueueRegionConfig,
    hasPriorQueue: boolean,
    region: RegionType
) => {
    const columnDefs: AgGridColumnDefs<
        ScenarioConstraint,
        ConstraintsTableColumnId
    > = [
        {
            colId: "projectId",
            field: "projectId",
            headerName: "Project",
            width: 125,
            pinned: true,
            filter: true
        },
        {
            colId: "queue",
            headerName: config.studyCycleLabel,
            pinned: true,
            width: 150,
            filter: true,
            valueGetter: ({ data }) => {
                if (!data) return "";
                const base = formatConstraintStudyCycle(
                    region,
                    getConstraintStudyCycle(data)
                );
                return isPriorQueueConstraint(data)
                    ? base
                        ? `${base} (PQ)`
                        : "PQ"
                    : base;
            },
            cellRenderer: ScenarioConstraintQueueCellRenderer,
            cellRendererParams: { region }
        },
        {
            colId: "monitoredFacilityName",
            field: "monitoredFacilityName",
            headerName: "Monitored facility",
            pinned: true,
            filter: true,
            width: 240
        },
        {
            colId: "area",
            field: "area",
            headerName: "Area",
            valueFormatter: stringFormatter,
            filter: true,
            width: 100
        },
        {
            colId: "constraintType",
            field: "constraintType",
            headerName: "Type",
            filter: true,
            width: 100,
            valueFormatter: constraintTypeFormatter
        },
        {
            colId: "dfax",
            field: "dfax",
            type: "numericColumn",
            headerName: "Dfax",
            width: 70,
            filter: true,
            valueFormatter: dfaxFormatter
        },
        {
            colId: "mwImpact",
            field: "mwImpact",
            type: "numericColumn",
            headerName: "MW Impact",
            width: 120,
            filter: true,
            valueFormatter: roundingFormatter
        },
        {
            headerName: "Load",
            children: [
                {
                    colId: "preStudyLoad",
                    field: "preStudyLoad",
                    type: "numericColumn",
                    headerName: config.loadLabel.benchLoad,
                    width: 100,
                    valueFormatter: percentValueFormatter
                },
                {
                    colId: "postStudyLoad",
                    field: "postStudyLoad",
                    type: "numericColumn",
                    headerName: config.loadLabel.studyLoad,
                    width: 110,
                    valueFormatter: percentValueFormatter
                }
            ]
        },
        {
            headerName: "Flow (MVA)",
            children: [
                {
                    colId: "preStudyFlow",
                    field: "preStudyFlow",
                    type: "numericColumn",
                    headerName: "Base",
                    width: 80,
                    valueFormatter: roundingFormatter
                },
                {
                    colId: "postStudyFlow",
                    field: "postStudyFlow",
                    type: "numericColumn",
                    headerName: "Study",
                    width: 80,
                    valueFormatter: roundingFormatter
                }
            ]
        },
        {
            colId: "rating",
            field: "postStudyRating",
            type: "numericColumn",
            headerName: "Rating (MVA)",
            width: 120
        },
        {
            colId: "worstContingency",
            field: "worstContingencyName",
            headerName: "Worst contingency",
            minWidth: 200,
            flex: 2,
            filter: true
        },
        {
            colId: "facilityDetails",
            field: "facilityDetails",
            headerName: "Facility type",
            width: 155,
            valueFormatter: stringFormatter
        },
        {
            colId: "networkUpgrades",
            field: "networkUpgrades",
            headerName: "Associated upgrades",
            minWidth: 200,
            flex: 2
        },
        {
            colId: "model",
            field: "model",
            headerName: "Model",
            valueFormatter: stringFormatter,
            width: 200
        },
        {
            colId: "notes",
            field: "notes",
            headerName: "Notes",
            valueFormatter: stringFormatter,
            minWidth: 300,
            flex: 1
        }
    ];

    // Hide the Study Cycle column for clusters without PQ contributors, via the
    // same columnsToRemove mechanism as every other conditional column.
    hideUnwantedColumns(columnDefs, [
        ...config.constraintsTable.columnsToRemove,
        ...(hasPriorQueue ? [] : (["queue"] as const))
    ]);

    return columnDefs;
};
